import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { FEATURE_FLAGS } from '../constants/featureFlags';

type NavLink = { label: string; to: string };
type NavGroup = { label: string; items: NavLink[] };
type NavEntry = NavLink | NavGroup;

const isGroup = (entry: NavEntry): entry is NavGroup => 'items' in entry;

const Header = () => {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [openDesktopMenu, setOpenDesktopMenu] = useState<string | null>(null);
  const [openMobileSection, setOpenMobileSection] = useState<string | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const desktopNavRef = useRef<HTMLElement>(null);
  const showPaymentRequests = useFeatureFlag(FEATURE_FLAGS.DELTA_PAY);
  const showReferrals = useFeatureFlag(FEATURE_FLAGS.REFERRAL_PROGRAMME);
  const isAdmin = user?.role === 'admin';
  const canViewInvoiceAnalytics = isAdmin || user?.role === 'manager';

  const navEntries = useMemo<NavEntry[]>(() => {
    const ordersItems: NavLink[] = [
      { label: 'Orders', to: '/orders' },
      { label: 'Refunds', to: '/refunds' },
      { label: 'Credit Transactions', to: '/credit-transactions' },
    ];
    if (showPaymentRequests) {
      ordersItems.push({ label: 'Payment Requests', to: '/payment-requests' });
    }
    if (canViewInvoiceAnalytics) {
      ordersItems.push({ label: 'Invoice Analytics', to: '/invoice-analytics' });
    }
    ordersItems.push({ label: 'Extra Income', to: '/extra-income' });

    const financesItems: NavLink[] = isAdmin
      ? [
          { label: 'Monthly Finance', to: '/finance-monthly-matrix' },
          { label: 'Reconciliation', to: '/finance-reconciliation' },
          { label: 'Order Financial Transactions', to: '/order-financial-transactions' },
          { label: 'Bank & External Transactions', to: '/external-financial-transactions' },
          { label: 'Import Bank Statement', to: '/import-external-transactions' },
          { label: 'Expense Claims', to: '/expense-claims' },
        ]
      : [];

    const customersItems: NavLink[] = [
      { label: 'Customers', to: '/customers' },
      { label: 'Message History', to: '/whatsapp-messages' },
    ];
    if (showReferrals) {
      customersItems.push({ label: 'Referral Discounts', to: '/referral-discounts' });
    }

    const entries: NavEntry[] = [
      { label: 'Dashboard', to: '/dashboard' },
      { label: 'AI Assistant', to: '/assistant' },
      { label: 'Orders', items: ordersItems },
      { label: 'Customers', items: customersItems },
      { label: 'Finances', items: financesItems },
      {
        label: 'Operations',
        items: [
          { label: 'Stores', to: '/stores' },
          { label: 'Pick-Up Points', to: '/pick-up-points' },
        ],
      },
    ];

    return entries
      .filter((entry) => !isGroup(entry) || entry.items.length > 0)
      .map((entry) =>
        isGroup(entry)
          ? { ...entry, items: [...entry.items].sort((a, b) => a.label.localeCompare(b.label)) }
          : entry
      );
  }, [isAdmin, canViewInvoiceAnalytics, showPaymentRequests, showReferrals]);

  const isLinkActive = (to: string) => pathname === to || pathname.startsWith(`${to}/`);
  const isEntryActive = (entry: NavEntry) =>
    isGroup(entry) ? entry.items.some((item) => isLinkActive(item.to)) : isLinkActive(entry.to);

  const handleSignOut = () => {
    logout();
    window.location.href = '/login';
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setOpenMobileSection(null);
  };

  const closeUserMenu = () => {
    setUserMenuOpen(false);
  };

  const closeDesktopMenu = () => {
    setOpenDesktopMenu(null);
  };

  const initial = user?.email ? user.email.split('@')[0].charAt(0).toUpperCase() : '';

  useEffect(() => {
    if (!userMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  useEffect(() => {
    if (!openDesktopMenu) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (desktopNavRef.current && !desktopNavRef.current.contains(event.target as Node)) {
        setOpenDesktopMenu(null);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenDesktopMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openDesktopMenu]);

  const desktopItemBase =
    'inline-flex items-center justify-center h-full px-3 py-2 text-sm font-medium text-gray-800 rounded whitespace-nowrap';
  const desktopItemClass = (active: boolean) =>
    `${desktopItemBase} ${active ? 'bg-yellow-200' : 'hover:bg-yellow-200'}`;
  const desktopDropdownItemClass = 'block px-4 py-2 text-sm text-gray-800 hover:bg-gray-100';
  const mobileItemClass = (active: boolean) =>
    `block px-4 py-3 text-base font-medium text-gray-800 rounded ${
      active ? 'bg-yellow-100' : 'hover:bg-yellow-100'
    }`;
  const mobileSubItemClass = 'block px-4 py-2 text-base font-medium text-gray-800 hover:bg-yellow-100 rounded';

  const chevron = (className: string) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );

  return (
    <>
      <header className="w-full flex items-center justify-between px-4 lg:px-6 py-4 bg-[#FCD34D] border-b border-gray-200 shadow-sm">
        <div className="flex items-center space-x-4 lg:space-x-8">
          {/* Hamburger Menu Button - Mobile Only */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-gray-800 hover:bg-yellow-200 rounded focus:outline-none focus:ring-2 focus:ring-gray-800"
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          <img src="/tini-logo.png" alt="Tini Express Logo" className="h-8 lg:h-10" />

          {/* Desktop Navigation - Hidden on Mobile */}
          <nav ref={desktopNavRef} className="hidden lg:flex space-x-2 h-10">
            {navEntries.map((entry) => {
              const active = isEntryActive(entry);

              if (!isGroup(entry)) {
                return (
                  <Link
                    key={entry.label}
                    to={entry.to}
                    className={desktopItemClass(active)}
                    aria-current={active ? 'page' : undefined}
                  >
                    {entry.label}
                  </Link>
                );
              }

              const open = openDesktopMenu === entry.label;
              return (
                <div key={entry.label} className="relative">
                  <button
                    onClick={() => setOpenDesktopMenu(open ? null : entry.label)}
                    className={`${desktopItemClass(active)} focus:outline-none focus:ring-2 focus:ring-gray-800`}
                    aria-haspopup="true"
                    aria-expanded={open}
                    aria-current={active ? 'true' : undefined}
                  >
                    {entry.label}
                    {chevron('w-4 h-4 ml-1')}
                  </button>
                  {open && (
                    <div className="absolute left-0 top-full mt-2 w-60 bg-white border border-gray-200 rounded shadow-lg z-50">
                      {entry.items.map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          onClick={closeDesktopMenu}
                          className={desktopDropdownItemClass}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        <div className="flex items-center space-x-2 lg:space-x-4">
          {process.env.REACT_APP_TESTING === 'true' ? (
            <span className="text-xs lg:text-sm font-medium text-orange-600 bg-orange-100 px-2 py-1 rounded">
              Test
            </span>
          ) : (
            <span className="text-xs lg:text-sm font-medium text-green-600 bg-green-100 px-2 py-1 rounded">
              Live
            </span>
          )}
          {user && (
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="w-9 h-9 rounded-full bg-gray-800 text-white font-bold flex items-center justify-center hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-800"
                aria-label="User menu"
                aria-haspopup="true"
                aria-expanded={userMenuOpen}
              >
                {initial}
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-gray-200 rounded shadow-lg z-50">
                  {isAdmin && (
                    <Link
                      to="/users"
                      onClick={closeUserMenu}
                      className="block px-4 py-2 text-sm text-gray-800 hover:bg-gray-100"
                    >
                      Users
                    </Link>
                  )}
                  <Link
                    to="/notification-preferences"
                    onClick={closeUserMenu}
                    className="block px-4 py-2 text-sm text-gray-800 hover:bg-gray-100"
                  >
                    Preferences
                  </Link>
                  <button
                    onClick={() => {
                      closeUserMenu();
                      handleSignOut();
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-gray-100"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Mobile Menu Drawer */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop Overlay */}
          <div
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={closeMobileMenu}
          />

          {/* Drawer */}
          <div className="lg:hidden fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-50 flex flex-col transform transition-transform duration-300 ease-in-out">
            {/* Drawer Header */}
            <div className="flex-shrink-0 flex items-center justify-between px-4 py-4 bg-[#FCD34D] border-b border-gray-200">
              <img src="/tini-logo.png" alt="Tini Express Logo" className="h-8" />
              <button
                onClick={closeMobileMenu}
                className="p-2 text-gray-800 hover:bg-yellow-200 rounded focus:outline-none focus:ring-2 focus:ring-gray-800"
                aria-label="Close menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Drawer Navigation Links */}
            <nav className="flex-1 overflow-y-auto flex flex-col p-4 space-y-2">
              {navEntries.map((entry) => {
                if (!isGroup(entry)) {
                  return (
                    <Link
                      key={entry.label}
                      to={entry.to}
                      onClick={closeMobileMenu}
                      className={mobileItemClass(isLinkActive(entry.to))}
                      aria-current={isLinkActive(entry.to) ? 'page' : undefined}
                    >
                      {entry.label}
                    </Link>
                  );
                }

                const open = openMobileSection === entry.label;
                return (
                  <div key={entry.label}>
                    <button
                      onClick={() => setOpenMobileSection(open ? null : entry.label)}
                      className="flex items-center justify-between w-full px-4 py-3 text-base font-medium text-gray-800 hover:bg-yellow-100 rounded focus:outline-none focus:ring-2 focus:ring-gray-800"
                      aria-haspopup="true"
                      aria-expanded={open}
                      aria-current={isEntryActive(entry) ? 'true' : undefined}
                    >
                      <span>{entry.label}</span>
                      {chevron(`w-4 h-4 transform transition-transform ${open ? 'rotate-180' : ''}`)}
                    </button>
                    {open && (
                      <div className="pl-4 mt-1 space-y-1">
                        {entry.items.map((item) => (
                          <Link
                            key={item.to}
                            to={item.to}
                            onClick={closeMobileMenu}
                            className={mobileSubItemClass}
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            {/* User Info in Drawer (Mobile) */}
            {user && (
              <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-gray-50 space-y-2">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gray-800 text-white font-bold flex items-center justify-center">
                    {initial}
                  </div>
                </div>
                {isAdmin && (
                  <Link
                    to="/users"
                    onClick={closeMobileMenu}
                    className="block px-4 py-3 text-base font-medium text-gray-800 hover:bg-yellow-100 rounded"
                  >
                    Users
                  </Link>
                )}
                <Link
                  to="/notification-preferences"
                  onClick={closeMobileMenu}
                  className="block px-4 py-3 text-base font-medium text-gray-800 hover:bg-yellow-100 rounded"
                >
                  Preferences
                </Link>
                <button
                  onClick={() => {
                    closeMobileMenu();
                    handleSignOut();
                  }}
                  className="block w-full text-left px-4 py-3 text-base font-medium text-red-500 hover:bg-yellow-100 rounded"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default Header;
